import type { FunnelConfig, ConfigProduct, ConfigStep, ConfigCategory } from '@/lib/hooks/useFunnelConfig'

export interface CombinationProduct {
  categoryId: string
  product: ConfigProduct
}

export interface Combination {
  /** Dedup key: funnelSlug + sorted wcIds */
  key: string
  funnelId: string
  funnelSlug: string
  baseImage: string
  products: CombinationProduct[]
  totalPrice: number
  wcIds: number[]
}

/**
 * Resolve which categories are visible given a set of question answers.
 */
function resolveVisibleCategories(
  configSteps: ConfigStep[],
  categories: ConfigCategory[],
  questionAnswers: Record<string, 'yes' | 'no'>
): ConfigCategory[] {
  const visibleCategoryIds = new Set<string>()

  for (const step of configSteps) {
    if (step.type === 'category' && step.categoryId) {
      visibleCategoryIds.add(step.categoryId)
    } else if (step.type === 'question' && step.id) {
      const answer = questionAnswers[step.id]
      if (answer === 'yes' && step.optionYes?.showCategories) {
        step.optionYes.showCategories.forEach(id => visibleCategoryIds.add(id))
      } else if (answer === 'no' && step.optionNo?.showCategories) {
        step.optionNo.showCategories.forEach(id => visibleCategoryIds.add(id))
      }
    }
  }

  const categoryMap = new Map(categories.map(c => [c.id, c]))
  return Array.from(visibleCategoryIds)
    .map(id => categoryMap.get(id))
    .filter((c): c is ConfigCategory => !!c)
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function randomQuestionAnswers(configSteps: ConfigStep[]): Record<string, 'yes' | 'no'> {
  const answers: Record<string, 'yes' | 'no'> = {}
  for (const step of configSteps) {
    if (step.type === 'question' && step.id) {
      answers[step.id] = Math.random() > 0.5 ? 'yes' : 'no'
    }
  }
  return answers
}

function generateOneCombination(config: FunnelConfig): Combination | null {
  const questionAnswers = randomQuestionAnswers(config.configSteps)
  const visibleCategories = resolveVisibleCategories(
    config.configSteps,
    config.categories,
    questionAnswers
  )

  const products: CombinationProduct[] = []
  const wcIds: number[] = []

  if (config.baseWcId) {
    wcIds.push(config.baseWcId)
  }

  for (const category of visibleCategories) {
    const inStockProducts = category.products.filter(p => p.inStock)
    if (inStockProducts.length === 0) continue

    if (category.required) {
      const product = pickRandom(inStockProducts)
      products.push({ categoryId: category.id, product })
      if (product.wcId) wcIds.push(product.wcId)
    } else {
      if (Math.random() < 0.7) {
        const product = pickRandom(inStockProducts)
        products.push({ categoryId: category.id, product })
        if (product.wcId) wcIds.push(product.wcId)
      }
    }
  }

  if (products.length === 0) return null

  const key = `${config.slug}:${[...wcIds].sort((a, b) => a - b).join('-')}`
  const totalPrice = config.basePrice + products.reduce((sum, p) => sum + p.product.price, 0)

  return {
    key,
    funnelId: config.id,
    funnelSlug: config.slug,
    baseImage: config.baseImage,
    products,
    totalPrice,
    wcIds,
  }
}

/**
 * Generate a batch of unique combinations from one config.
 */
export function generateCombinations(
  config: FunnelConfig,
  count: number,
  seenKeys: Set<string>
): Combination[] {
  const results: Combination[] = []
  let attempts = 0
  const maxAttempts = count * 10

  while (results.length < count && attempts < maxAttempts) {
    attempts++
    const combo = generateOneCombination(config)
    if (combo && !seenKeys.has(combo.key)) {
      seenKeys.add(combo.key)
      results.push(combo)
    }
  }

  return results
}

/**
 * Generate combinations from multiple configs, evenly distributed and shuffled.
 */
export function generateMultiCombinations(
  configs: FunnelConfig[],
  count: number,
  seenKeys: Set<string>
): Combination[] {
  if (configs.length === 0) return []
  if (configs.length === 1) return generateCombinations(configs[0], count, seenKeys)

  // Generate roughly equal amounts per config
  const perConfig = Math.ceil(count / configs.length)
  const all: Combination[] = []

  for (const config of configs) {
    const combos = generateCombinations(config, perConfig, seenKeys)
    all.push(...combos)
  }

  // Shuffle (Fisher-Yates)
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]]
  }

  return all.slice(0, count)
}
